<template>
  <v-col>
    <v-row>
      <v-card-subtitle
        class="subtitle-1 font-weight-medium darkestblue--text"
      >
        <v-row>
          <v-col>
            <span :class="inDarkMode ? 'white--text' : 'darkblue--text'">
              Group
            </span>

            <font-awesome-icon
              :icon="['fas', 'circle-question']"
              :class="`ml-1 ${showInfo ? 'primary' : 'midgray'}--text`"
              title="Instructions"
              @click="toggleInfo"
            />
          </v-col>
        </v-row>

        <v-row
          v-if="showInfo"
          class="mt-0"
        >
          <v-col>
            <p class="caption coolgray--text">
              Group rows by related properties to create data breakdowns.
            </p>

            <p class="caption coolgray--text">
              <b>Example</b> Selecting Order will create an order-level breakdown
              rendering a table per order.
            </p>

            <p class="mb-0 caption coolgray--text">
              <b>Hint</b> Combine dimensions below for even deeper data drilling.
            </p>
          </v-col>
        </v-row>
      </v-card-subtitle>
    </v-row>

    <v-row class="px-4">
      <v-select
        v-model="groupedBy"
        label="Property"
        :items="currentGroupingOptions"
        dense
        multiple
        clearable
      >
        <template v-slot:prepend-item>
          <v-list-item>
            <v-list-item-content ripple>
              <v-list-item-title class="cursor-pointer align-center row">
                <v-col class="col-1">
                  <Checkbox
                    :input-value="hasSelectedAll"
                    :color="hasSelectedAll ? 'primary' : 'grey'"
                    @change="toggleSelectAll('select')"
                  />
                </v-col>

                <v-col
                  :class="`pl-2 ${inDarkMode ? 'white' : 'black'}--text font-weight-medium text-wrap body-2`"
                  @click="toggleSelectAll('select')"
                >
                  <span>Select All</span>
                </v-col>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item>
            <v-list-item-content ripple>
              <v-list-item-title class="cursor-pointer align-center row">
                <v-col
                  class="col-1"
                  @click="toggleSelectAll('toggle')"
                >
                  <font-awesome-icon
                    :icon="['fas', 'list-check']"
                    class="grey--text"
                  />
                </v-col>

                <v-col
                  :class="`pl-2 ${inDarkMode ? 'white' : 'black'}--text font-weight-medium text-wrap body-2`"
                  @click="toggleSelectAll('toggle')"
                >
                  <span>Toggle All</span>
                </v-col>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-divider class="mt-2" />
        </template>

        <template v-slot:item="option">
          <v-list-item @on="option.on">
            <v-list-item-content class="py-0">
              <v-list-item-title class="cursor-pointer align-center row">
                <v-col class="col-1">
                  <Checkbox
                    :input-value="groupedBy.includes(option.item.value)"
                    :color="groupedBy.includes(option.item.value) ? 'primary' : 'midgray'"
                    @change="toggleOption(option.item)"
                  />
                </v-col>

                <v-col
                  :class="`pl-2 ${inDarkMode ? 'white' : 'black'}--text font-weight-medium text-wrap body-2`"
                  @click="toggleOption(option.item)"
                >
                  <span>{{ option.item.text || option.item.value }}</span>
                </v-col>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>

        <template v-slot:no-data>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="align-center row">
                <span :class="`grey--text text--lighten-1 body-2`">
                  No grouping options available
                </span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-select>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import Checkbox from '@/tools/components/Checkbox.vue'

const { mapGetters, mapState } = createNamespacedHelpers('tools')
const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'GroupTool',
  components: {
    Checkbox,
  },
  data() {
    return {
      showInfo: false,
    }
  },
  computed: {
    ...mapAuthGetters(['inDarkMode']),
    ...mapGetters([
      'currentGroupByProperties',
      'currentGrouping',
      'currentGroupingOptions',
      'reportKey',
    ]),
    ...mapState({
      isCurrentlyGrouping: (state) => state.isCurrentlyGrouping,
    }),

    groupedBy: {
      get() {
        return (this.isCurrentlyGrouping && this.currentGrouping) || this.currentGroupByProperties
      },
      set(groupByProperties) {
        const { reportKey } = this

        this.$store.commit('tools/isCurrentlyGrouping', true)
        this.$store.commit('tools/grouping', { groupByProperties, reportKey })

        return groupByProperties
      },
    },
    hasSelectedAll() {
      const { currentGroupingOptions, groupedBy } = this
      const totalSelected = groupedBy.length
      const totalOptions = currentGroupingOptions.length
      const hasSelectedAll = totalSelected === totalOptions

      return totalOptions > 0 && hasSelectedAll
    },
    hasSelectedSome() {
      return this.groupedBy.length > 0 && !this.hasSelectedAll
    },
  },
  methods: {
    toggleInfo() {
      this.showInfo = !this.showInfo
    },
    toggleSelectAll(selectOrToggle) {
      const { hasSelectedAll } = this
      const isToggling = selectOrToggle === 'toggle'

      const selectAll = () => {
        this.groupedBy = this.currentGroupingOptions.map((option) => option.value)
      }
      const unselectAll = () => {
        this.groupedBy = []
      }
      const toggleAll = () => {
        const filterSelected = (option) => !this.groupedBy.includes(option.value)
        this.groupedBy = this.currentGroupingOptions.filter(filterSelected).map((option) => option.value)
      }

      if (isToggling) {
        toggleAll()
      } else if (hasSelectedAll) {
        unselectAll()
      } else {
        selectAll()
      }
    },
    toggleOption(item) {
      const index = this.groupedBy.indexOf(item.value)

      if (index > -1) {
        // Remove item
        const newGroupedBy = [...this.groupedBy]
        newGroupedBy.splice(index, 1)
        this.groupedBy = newGroupedBy
      } else {
        // Add item
        this.groupedBy = [...this.groupedBy, item.value]
      }
    },
  },
}
</script>
